/**
 * Satellite detail enricher: real catalog identity + orbital parameters derived
 * from the propagated mean elements. Shows the TRUE altitude (never the
 * exaggerated 3D display shell) and the live sub-satellite point.
 */

import type { Track } from '@vtak/shared';
import type { MarkerData } from '@/map/markers/marker-state';
import { type EntityDetail, type DetailRow, formatCoords, textRow } from './entity-detail';
import { computeOrbit } from './satellite-orbit';

/** Build the satellite detail view from the track's orbital props. */
export function enrichSatellite(data: MarkerData, track: Track | undefined): EntityDetail {
  const props = track?.props ?? {};
  const altitudeKm = numberProp(props, 'altitudeKm');
  const meanMotion = numberProp(props, 'meanMotion');
  const eccentricity = numberProp(props, 'eccentricity');
  const inclination = numberProp(props, 'inclination');
  const orbit = computeOrbit(altitudeKm, meanMotion, eccentricity);

  return {
    sections: [
      {
        title: 'Catalog',
        rows: [
          textRow('NORAD ID', props['noradId']),
          textRow("Int'l Designator", props['intlDesignator']),
          textRow('Constellation', constellationLabel(track?.category)),
          textRow('Orbit Class', props['orbitClass']),
          textRow('Epoch', formatEpoch(props['epoch'])),
        ],
      },
      {
        title: 'Orbit',
        rows: [
          km('Altitude', altitudeKm),
          { label: 'Period', value: `${orbit.periodMinutes.toFixed(1)} min` },
          { label: 'Speed', value: `${orbit.speedKmps.toFixed(2)} km/s` },
          { label: 'Inclination', value: `${inclination.toFixed(2)}°` },
          { label: 'Eccentricity', value: eccentricity.toFixed(4) },
          km('Apogee', orbit.apogeeKm),
          km('Perigee', orbit.perigeeKm),
        ],
      },
      {
        title: 'Ground Coverage',
        rows: [
          km('Footprint radius', orbit.footprintRadiusKm),
          { label: 'Sub-satellite point', value: formatCoords(data.position) },
        ],
      },
    ],
  };
}

/** Read a numeric track prop, defaulting to 0. */
function numberProp(props: Record<string, unknown>, key: string): number {
  const value = props[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

/** A kilometre-valued row (rounded, thousands-separated). */
function km(label: string, value: number): DetailRow {
  return { label, value: `${Math.round(value).toLocaleString()} km` };
}

/** Format an ISO epoch to a short UTC date-time, or blank. */
function formatEpoch(epoch: unknown): string {
  if (typeof epoch !== 'string') return '';
  const date = new Date(epoch);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString([], { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}
